/**
 * ConversionTab.js - Tab de conversión y pendientes de activar
 */
import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../../../services/api";

function ConversionTab({ metricas }) {
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar pendientes
  const loadPendientes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/objetos/mkt/pendientes?limit=100', { headers });

      if (!response.ok) {
        throw new Error('Error al cargar pendientes');
      }

      const data = await response.json();
      setPendientes(data.data || []);
    } catch (err) {
      console.error('Error cargando pendientes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendientes();
  }, [loadPendientes]);

  // Calcular días desde liberación
  const calcularDias = (fecha) => {
    if (!fecha) return 0;
    const liberado = new Date(fecha);
    const hoy = new Date();
    const diff = hoy - liberado;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Clasificar por días
  const clasificarDias = (dias) => {
    if (dias <= 3) return 'normal';
    if (dias <= 7) return 'warning';
    return 'danger';
  };

  // Agrupar por tipo
  const pendientesPorTipo = pendientes.reduce((acc, p) => {
    const tipo = p.tipo || 'otro';
    if (!acc[tipo]) acc[tipo] = [];
    acc[tipo].push(p);
    return acc;
  }, {});

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const vendidos = metricas?.vendidos || 0;
  const activados = metricas?.activados || 0;
  const tasaConversion = metricas?.tasaConversion || 0;
  const pendientesCount = metricas?.pendientesActivar || pendientes.length;

  return (
    <div className="conversion-tab">
      {/* Métricas de conversión */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="conversion-funnel">
            <div className="row align-items-center justify-content-center text-center g-3">
              <div className="col-auto">
                <div className="funnel-box">
                  <div className="funnel-value text-success">{vendidos}</div>
                  <div className="funnel-label">Vendidos</div>
                </div>
              </div>
              <div className="col-auto">
                <i className="bi bi-arrow-right text-muted fs-4"></i>
              </div>
              <div className="col-auto">
                <div className="funnel-box">
                  <div className="funnel-value text-info">{activados}</div>
                  <div className="funnel-label">Activados</div>
                </div>
              </div>
              <div className="col-auto">
                <i className="bi bi-arrow-right text-muted fs-4"></i>
              </div>
              <div className="col-auto">
                <div className="funnel-box" style={{
                  borderColor: pendientesCount > 0 ? '#ffc107' : '#198754',
                  borderWidth: '2px',
                  borderStyle: 'solid'
                }}>
                  <div className="funnel-value" style={{ color: pendientesCount > 0 ? '#ffc107' : '#198754' }}>
                    {pendientesCount}
                  </div>
                  <div className="funnel-label">Pendientes</div>
                </div>
              </div>
              <div className="col-12 col-md-auto">
                <div className="d-flex flex-column align-items-center">
                  <div className="display-4 fw-bold" style={{
                    color: tasaConversion >= 90 ? '#198754' : tasaConversion >= 70 ? '#ffc107' : '#dc3545'
                  }}>
                    {tasaConversion}%
                  </div>
                  <div className="text-muted small">Tasa de Conversión</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de pendientes */}
      <div className="card">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="bi bi-hourglass-split me-2 text-warning"></i>
            Vendidos Sin Activar
            {pendientes.length > 0 && (
              <span className="badge bg-warning text-dark ms-2">{pendientes.length}</span>
            )}
          </h6>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={loadPendientes}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>

        <div className="card-body">
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-warning" role="status"></div>
              <span className="ms-2 text-muted">Cargando pendientes...</span>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {!loading && pendientes.length === 0 && !error && (
            <div className="text-center py-5">
              <i className="bi bi-emoji-smile display-1 text-success"></i>
              <h5 className="mt-3 text-success">Excelente</h5>
              <p className="text-muted">Todos los tags vendidos han sido activados</p>
            </div>
          )}

          {!loading && pendientes.length > 0 && (
            <>
              {/* Leyenda */}
              <div className="mb-3 d-flex gap-3 flex-wrap">
                <span className="small">
                  <span className="dias-badge normal">0-3 días</span> Normal
                </span>
                <span className="small">
                  <span className="dias-badge warning">4-7 días</span> Atención
                </span>
                <span className="small">
                  <span className="dias-badge danger">8+ días</span> Seguimiento
                </span>
              </div>

              {/* Lista por tipo */}
              {Object.entries(pendientesPorTipo).map(([tipo, items]) => (
                <div key={tipo} className="mb-4">
                  <h6 className="text-muted mb-3">
                    <i className={`bi ${tipo === 'mascota' ? 'bi-heart' : tipo === 'llavero' ? 'bi-key' : 'bi-gem'} me-2`}></i>
                    {tipo.charAt(0).toUpperCase() + tipo.slice(1)}s
                    <span className="badge bg-secondary ms-2">{items.length}</span>
                  </h6>

                  {items
                    .sort((a, b) => calcularDias(b.liberadoAt) - calcularDias(a.liberadoAt))
                    .map((item) => {
                      const dias = calcularDias(item.liberadoAt);
                      const clase = clasificarDias(dias);

                      return (
                        <div key={item.id} className={`pendiente-item ${clase}`}>
                          <div className="row align-items-center">
                            <div className="col">
                              <code className="text-primary">{item.numeroSerie || item.folio}</code>
                              <span className={`badge ms-2 ${item.plan === 'premium' ? 'bg-warning text-dark' : item.plan === 'standard' ? 'bg-info' : 'bg-secondary'}`}>
                                {item.plan}
                              </span>
                            </div>
                            <div className="col-auto text-muted small">
                              <i className="bi bi-calendar me-1"></i>
                              {formatearFecha(item.liberadoAt)}
                            </div>
                            <div className="col-auto">
                              <span className={`dias-badge ${clase}`}>
                                {dias === 0 ? 'Hoy' : dias === 1 ? '1 día' : `${dias} días`}
                                {clase === 'danger' && <i className="bi bi-exclamation-triangle ms-1"></i>}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Consejos de seguimiento */}
      {!loading && pendientes.length > 0 && (
        <div className="card mt-4">
          <div className="card-header bg-light">
            <h6 className="mb-0">
              <i className="bi bi-lightbulb me-2 text-warning"></i>
              Consejos de Seguimiento
            </h6>
          </div>
          <div className="card-body">
            <ul className="mb-0">
              <li className="mb-2">
                <strong>0-3 días:</strong> Normal, el cliente puede estar ocupado o aún recibiendo el producto.
              </li>
              <li className="mb-2">
                <strong>4-7 días:</strong> Considera enviar un recordatorio amable por email o WhatsApp.
              </li>
              <li className="mb-2">
                <strong>8+ días:</strong> Contactar directamente para verificar si hay algún problema con la activación.
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversionTab;
